import { t } from 'i18next';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import cn from '../../../utils/cn';

import styles from './ChartPie.module.css';

const CHART_HEIGHT = 280;

/**
 * Format a pie legend label, optionally appending the slice value.
 * @param {string} value - Slice name
 * @param {{ payload?: { value?: number } }} entry - Recharts legend entry
 * @param {boolean} showValuesInLegend - Whether to append the numeric value
 * @returns {string} Legend label
 */
function formatPieLegendLabel(value, entry, showValuesInLegend) {
  if (!showValuesInLegend) return value;
  const sliceValue = entry?.payload?.value;
  if (sliceValue === undefined || sliceValue === null) return value;
  return `${value} (${sliceValue})`;
}

/**
 * Pie chart for dashboard (Recharts PieChart).
 * @param {object} props
 * @param {MappedPieSlice[]} props.slices - Pie slices
 * @param {MappedChartLegend | null} [props.legend] - Legend config, null to hide
 * @param {boolean} [props.showValuesInLegend=false] - Show value next to the legend label
 * @param {boolean} [props.donut=false] - Render as a donut
 * @returns {JSX.Element}
 */
function ChartPie({
  slices,
  legend = null,
  showValuesInLegend = false,
  donut = false,
}) {
  if (!slices?.length) {
    return <p className={styles['chart-pie-empty']}>{t('dashboard.emptyChart')}</p>;
  }

  return (
    <div
      className={cn([
        styles['chart-pie'],
        legend?.layout === 'horizontal' && styles['chart-pie-legend-horizontal'],
      ])}
    >
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <PieChart>
          <Pie
            data={slices}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={donut ? '55%' : 0}
            outerRadius="80%"
            paddingAngle={donut ? 2 : 0}
          >
            {slices.map((slice) => (
              <Cell key={slice.name} fill={slice.color} />
            ))}
          </Pie>
          <Tooltip />
          {legend ? (
            <Legend
              layout={legend.layout}
              align={legend.align}
              verticalAlign={legend.verticalAlign}
              itemSorter={null}
              formatter={(value, entry) => formatPieLegendLabel(value, entry, showValuesInLegend)}
            />
          ) : null}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

export default ChartPie;
